import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import NextLink from "next/link"
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { Mobile, useIsDesktop } from "ui"
import { applicationRequestSuccess, useFocusOnError } from "utils"
import { regex } from "utils/FormSchema"
import * as yup from "yup"
import { CustomTextField } from "./Common"

type Props = {
  locale: "he" | "en" | "ar" | "ru"
  label: string
}

interface FineByIdResult {
  fineId: number
  fineNumerator: number
  paymentTypeDesc: string | null
  paymentStatusDesc: string | null
  supplierPhone: string | null
  supplierName: string | null
}

interface FormData {
  identityNumber: string
}

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 16px;
  }
`

const StyledMain = styled.div`
  box-shadow: 0px 2px 8px 0px #26303d29;
  border-radius: 12px;
  padding: 24px;
`

const StyledTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`

const StyledLine = styled.div`
  width: 1px;
  background-color: #d1d5db;
  height: 24px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    display: none;
  }
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 32px;
    padding-top: 32px;
    width: fit-content;
  }
  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-top: 16px;
  }
  .MuiFormControl-root {
    margin-block-end: 0;
    ${({ theme }) => theme.breakpoints.up("md")} {
      width: 352px;
      flex-shrink: 0;
    }
  }
  .MuiTextField-root {
    margin-block-end: 0;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 56px;
  flex-shrink: 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
  }
`

const StyledResultTitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`

const StyledDot = styled.div<{ color: string }>`
  min-width: 12px;
  min-height: 12px;
  border-radius: 200px;
  border: 3px solid white;
  background-color: ${(props) => props.color};
  box-shadow: 0px 0px 16px 0px ${(props) => props.color};
  margin-top: 2px;
`

const StyledFineCard = styled.div`
  background: #fff;
  box-shadow: 0px 2px 8px 0px #26303d29;
  border-radius: 12px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const StyledStatusRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  border-bottom: 1px solid #d1d5db;
  padding-bottom: 24px;
  padding-top: 8px;
`

const StyledContentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 32px;
  }
`

const StyledDetailsGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  flex-shrink: 0;
  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column;
    gap: 12px;
  }
`

const StyledDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  background: #fcfcfc;
  border-radius: 12px;
  padding: 12px;
  height: 56px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 352px;
  }
`

const StyledPayButton = styled(Button)`
  height: 56px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
    margin-inline-start: auto;
  }
`

const StyledCollectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledPhoneLink = styled.a`
  color: #005fd3;
  text-decoration: underline;
  direction: ltr;
  unicode-bidi: isolate;
`

const StyledResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

function FineDetailsGrid({
  fine,
  t,
}: {
  fine: FineByIdResult
  t: ReturnType<typeof useTranslations<"Forms">>
}) {
  return (
    <StyledDetailsGrid>
      <StyledDetailItem>
        <Typography variant="body2" color="text.secondary">
          {t("penaltyReportNumber")}
        </Typography>
        <Typography variant="body1">{fine.fineId}</Typography>
      </StyledDetailItem>
      <StyledDetailItem>
        <Typography variant="body2" color="text.secondary">
          {t("penaltyFormNumber")}
        </Typography>
        <Typography variant="body1">{fine.fineNumerator}</Typography>
      </StyledDetailItem>
    </StyledDetailsGrid>
  )
}

export default function PenaltyLookup({ locale, label }: Props) {
  const t = useTranslations("Forms")
  const tInner = useTranslations("InnerPages")
  const [results, setResults] = useState<FineByIdResult[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isDesktop = useIsDesktop()
  const resultsRef = useRef<HTMLDivElement>(null)

  const schema = yup.object({
    identityNumber: yup
      .string()
      .matches(regex.identity, t("penaltyIdInvalid"))
      .required(t("penaltyIdRequired")),
  })

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      identityNumber: "",
    },
  })

  useFocusOnError(errors, isSubmitted)

  useEffect(() => {
    if (results !== null && results.length > 0) {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [results])

  const onSubmit = async (data: FormData) => {
    setIsSearching(true)
    setError(null)
    try {
      const response = await fetch(
        `/api/fines/byid?identityNumber=${data.identityNumber}`,
      )
      if (!response.ok) {
        setError(t("penaltySearchError"))
        setResults(null)
        return
      }
      const fines = await response.json()
      setResults(Array.isArray(fines) ? fines : [])
      applicationRequestSuccess('שחזור דו"ח (מעריף מוגדל)', label)
    } catch {
      setError(t("penaltySearchError"))
      setResults(null)
    } finally {
      setIsSearching(false)
    }
  }

  const currentId = watch("identityNumber")
  const displayableFines =
    results?.filter((f) => f.paymentStatusDesc !== "מבוטל") ?? []
  const pendingFines = displayableFines.filter(
    (f) => !f.supplierName && !f.supplierPhone,
  )
  const collectionFines = displayableFines.filter(
    (f) => !!f.supplierName || !!f.supplierPhone,
  )

  const getPaymentUrl = (fine: FineByIdResult) =>
    `/${locale}/information-for-passengers/payment-of-an-increased-rate-report?fineId=${fine.fineId}&fineNumerator=${fine.fineNumerator}`

  const getResultTitle = () => {
    if (!results || displayableFines.length === 0) return ""
    if (displayableFines.length === 1) {
      return t("penaltyReportFound", { id: currentId })
    }
    return t("penaltyReportsFound", {
      count: displayableFines.length,
      id: currentId,
    })
  }

  return (
    <Layout>
      <StyledMain>
        <StyledTextWrapper>
          <Typography variant="h4">{tInner("personalDetails")}</Typography>
          <StyledLine />
          {isDesktop && (
            <Typography variant="body3">
              {tInner("mandatoryFieldWithAsterisk")}
              <span style={{ color: "rgb(224, 7, 0)" }}>*</span>
            </Typography>
          )}
        </StyledTextWrapper>
        <Mobile>
          <Typography variant="body3">
            {tInner("mandatoryFieldWithAsterisk")}
            <span style={{ color: "rgb(224, 7, 0)" }}>*</span>
          </Typography>
        </Mobile>

        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <CustomTextField
            name="identityNumber"
            control={control}
            label={t("penaltyIdNumber")}
            required
            isSubmitted={isSubmitted}
            errors={errors}
            maxLength={9}
          />
          <StyledButton variant="primary" type="submit" disabled={isSearching}>
            {t("penaltySearch")}
          </StyledButton>
        </FormContainer>
      </StyledMain>

      {error && (
        <StyledResultTitle>
          <StyledDot color="#E00700" />
          <Typography variant="h4">{error}</Typography>
        </StyledResultTitle>
      )}

      {results !== null && !error && (
        <StyledResultsSection ref={resultsRef}>
          {displayableFines.length === 0 ? (
            <StyledResultTitle>
              <StyledDot color="#E00700" />
              <Typography variant="h4">{t("penaltyNoReport")}</Typography>
            </StyledResultTitle>
          ) : (
            <>
              <Typography variant="h4">{getResultTitle()}</Typography>

              {pendingFines.map((fine) => (
                <StyledFineCard key={`${fine.fineId}-${fine.fineNumerator}`}>
                  <StyledStatusRow>
                    <StyledDot color="#1BBD77" />
                    <Typography variant="body1" fontWeight={700}>
                      {t("penaltyPendingPayment")}
                    </Typography>
                  </StyledStatusRow>

                  <Typography variant="h4">
                    {t("penaltyReportDetails")}
                  </Typography>

                  <StyledContentRow>
                    <FineDetailsGrid fine={fine} t={t} />
                    <StyledPayButton
                      variant="primary"
                      component={NextLink}
                      href={getPaymentUrl(fine)}
                    >
                      {t("penaltyPayButton")}
                    </StyledPayButton>
                  </StyledContentRow>
                </StyledFineCard>
              ))}

              {collectionFines.map((fine) => (
                <StyledFineCard key={`${fine.fineId}-${fine.fineNumerator}`}>
                  <StyledStatusRow>
                    <StyledDot color="#F8A401" />
                    <Typography variant="body1" fontWeight={700}>
                      {t("penaltyInCollection")}
                    </Typography>
                  </StyledStatusRow>

                  <Typography variant="h4">
                    {t("penaltyReportDetails")}
                  </Typography>

                  <FineDetailsGrid fine={fine} t={t} />

                  {fine.supplierName && (
                    <StyledCollectionInfo>
                      <Typography variant="body1" fontWeight={700}>
                        {t("penaltyCollectionCompany")} {fine.supplierName}
                      </Typography>
                      {fine.supplierPhone && (
                        <Typography variant="body2">
                          {t("penaltyCompanyPhone")}{" "}
                          <StyledPhoneLink dir="ltr" href={`tel:${fine.supplierPhone}`}>
                            {fine.supplierPhone}
                          </StyledPhoneLink>
                        </Typography>
                      )}
                    </StyledCollectionInfo>
                  )}
                </StyledFineCard>
              ))}
            </>
          )}
        </StyledResultsSection>
      )}
    </Layout>
  )
}
